import { createLogger } from '@/lib/logger'

import { desktopAuthFlow, inAppOAuthFlow } from './tauri'

const SIGN_IN_TIMEOUT_MS = 300000

const logger = createLogger('lib/utils/desktop-sign-in')

/**
 * True only under `tauri dev`, never in a build.
 *
 * Every desktop build script runs a plain `vite build`, which means mode
 * `production`, which means `import.meta.env.DEV` is false — in the local,
 * dev-channel and production apps alike. The one case left is the desktop dev
 * loop, where the webview is pointed at the vite dev server on :3005.
 */
const IS_TAURI_DEV_LOOP = import.meta.env.DEV

interface DesktopSignInOptions {
  authUrl: string
  /**
   * Exchange the code for tokens. Only reached in the `tauri dev` loop; the
   * shipped path gets its tokens back over the deep link instead.
   */
  exchange: (code: string, state: string) => Promise<unknown>
  /** Show the provider's account picker instead of reusing the last account. */
  forceNewAccount?: boolean
}

/**
 * Sign in to a federated provider from the desktop app.
 *
 * Shipped builds hand the consent screen to the user's real default browser
 * and wait for tokens to arrive on the `scrambl://` deep link. That is the only
 * flow Google's OAuth policy accepts: an embedded webview is refused outright,
 * and navigating the Tauri shell to the provider would replace the whole app.
 *
 * The `tauri dev` loop is the sole exception, and it is not a preference. On
 * macOS the URL scheme is registered from the bundled .app's Info.plist, and
 * `tauri dev` runs an unbundled binary — so a deep link has nothing to come
 * back to and the browser flow would hang forever. There the in-app OAuth
 * window intercepts the redirect and hands the code over directly.
 *
 * Keeping that exception in one place is deliberate: it means the embedded
 * webview cannot reach a build without this constant changing.
 */
export async function runDesktopSignIn({
  authUrl,
  exchange,
  forceNewAccount,
}: DesktopSignInOptions): Promise<void> {
  if (IS_TAURI_DEV_LOOP) {
    logger.info('[DesktopSignIn] tauri dev loop — using the in-app OAuth window')
    const { code, state } = await inAppOAuthFlow(
      authUrl,
      '/auth/callback',
      SIGN_IN_TIMEOUT_MS,
    )
    await exchange(code, state || '')
    return
  }

  await desktopAuthFlow(authUrl, SIGN_IN_TIMEOUT_MS, { forceNewAccount })
}
